import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import apiService from '../services/api.js';
import { useAuth } from '../contexts/AuthContext.js';
import Navigation from '../components/Navigation.js';
import { LAYOUT_CONFIG } from '../constants/layout.js';

const RecordingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { audioId: urlAudioId } = useParams();
  const [searchParams] = useSearchParams();
  const autoStart = searchParams.get('autostart') === '1';

  // API基础URL
  const API_BASE_URL = process.env.REACT_APP_WORKERS_API_URL || 'http://localhost:3002';

  // 健康状态
  const [healthStatus, setHealthStatus] = useState('checking');
  const [healthError, setHealthError] = useState(null);

  // 录音状态
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [segments, setSegments] = useState([]);
  const [error, setError] = useState('');
  const [uploadInterval] = useState(parseInt(process.env.REACT_APP_AUDIO_SEGMENT_DURATION) || 5); // 从环境变量读取分段时长，默认5秒
  const [isStopping, setIsStopping] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // 统一的提示状态
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // 润色相关状态
  const [isRecordingStopped, setIsRecordingStopped] = useState(false);
  const [isQueueEmpty, setIsQueueEmpty] = useState(false);
  const [showPolishUI, setShowPolishUI] = useState(false);
  const [polishInstruction, setPolishInstruction] = useState('');
  const [polishedText, setPolishedText] = useState('');
  const [isPolishing, setIsPolishing] = useState(false);
  const [transcriptionText, setTranscriptionText] = useState('');
  const [recordCreated, setRecordCreated] = useState(false);
  const [recordCreateError, setRecordCreateError] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [hasStartedRecording, setHasStartedRecording] = useState(false); // 标记是否已开始过录音
  const [polishMode, setPolishMode] = useState('original'); // 润色模式：'original' 基于原始转写，'polished' 基于润色结果
  const isNewRecordingRef = useRef(false); // 标记是否是正在进行的新录音

  // 用于跟踪原始内容，用于onBlur时比较是否有变更
  const polishedTextRef = useRef('');
  const polishInstructionRef = useRef('');
  const transcriptionTextRef = useRef('');

  // 缓存管理配置
  const [cacheConfig] = useState({
    autoCleanup: true,
    cleanupDelay: 30000,
    maxCacheSize: parseInt(process.env.REACT_APP_CACHE_MAX_SIZE) || 5,
    cleanupOnCompleted: true
  });

  // 录音器相关
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const splitTimerRef = useRef(null);
  const segmentIndexRef = useRef(1);
  const audioChunksRef = useRef([]);
  const audioIdRef = useRef(null);
  const isRecordingRef = useRef(false);
  const segmentStartTimeRef = useRef(null);
  const recordingStartTimeRef = useRef(null);

  // 本地音频缓存
  const audioSegmentsRef = useRef(new Map());
  const cacheCleanupTimersRef = useRef({});
  const currentlyPlayingRef = useRef(null);

  // 任务队列管理
  const taskQueueRef = useRef([]);
  const isProcessingQueueRef = useRef(false);
  const failedTasksRef = useRef({});
  const retryTimersRef = useRef({});
  const isCancellingRef = useRef(false); // 区分取消录音和正常停止

  // 设备管理相关
  const [availableDevices, setAvailableDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);

  // textarea refs for auto-resize
  const polishedTextareaRef = useRef(null);
  const instructionTextareaRef = useRef(null);

  // 格式化时间显示
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // 获取可用的音频设备
  const getAudioDevices = async () => {
    setIsLoadingDevices(true);
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(device => device.kind === 'audioinput');
      console.log('🎤 检测到音频输入设备:', audioInputs);
      setAvailableDevices(audioInputs);

      // 查找系统默认设备（deviceId为'default'的设备）
      const systemDefaultDevice = audioInputs.find(device => device.deviceId === 'default');

      // 如果还没有选择设备，优先选择系统默认设备
      if (!selectedDeviceId && audioInputs.length > 0) {
        if (systemDefaultDevice) {
          setSelectedDeviceId(systemDefaultDevice.deviceId);
          console.log(`🎯 自动选择系统默认设备: ${systemDefaultDevice.label || systemDefaultDevice.deviceId}`);
        } else {
          // 如果没有'default'设备，选择第一个可用设备
          const firstDevice = audioInputs[0];
          setSelectedDeviceId(firstDevice.deviceId);
          console.log(`🎯 自动选择第一个可用设备: ${firstDevice.label || firstDevice.deviceId}`);
        }
      }

      return audioInputs;
    } catch (error) {
      console.error('❌ 获取音频设备失败:', error);
      return [];
    } finally {
      setIsLoadingDevices(false);
    }
  };

  // 选择录音设备
  const selectDevice = (deviceId) => {
    console.log(`🎯 选择录音设备: ${deviceId}`);
    setSelectedDeviceId(deviceId);
  };

  // 监听设备变化
  useEffect(() => {
    const handleDeviceChange = () => {
      console.log('🔄 音频设备发生变化');
      getAudioDevices();
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

    // 初始获取设备列表
    getAudioDevices();

    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, []);

  // 显示保存成功消息
  const showSaveSuccessMessage = useCallback((message) => {
    // 先设置消息，再显示，避免闪烁
    setSaveMessage(message);
    requestAnimationFrame(() => {
      setShowSaveSuccess(true);
      setTimeout(() => {
        setShowSaveSuccess(false);
        // 延迟清空消息，避免组件卸载时的闪烁
        setTimeout(() => {
          setSaveMessage('');
        }, 100);
      }, 1500);
    });
  }, []);

  // 自动调整textarea高度的函数
  const autoResizeTextarea = useCallback((textarea) => {
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  }, []);

  // 片段状态枚举
  const SegmentStatus = {
    PENDING: 'pending',
    UPLOADING: 'uploading',
    UPLOADED: 'uploaded',
    TRANSCRIBING: 'transcribing',
    COMPLETED: 'completed',
    FAILED: 'failed',
    RETRYING: 'retrying'
  };

  // 生成录音会话ID
  const generateAudioId = () => {
    return uuidv4();
  };

  // 上传和转写函数
  const uploadAndTranscribe = async (audioBlob, audioId, segmentIndex, duration) => {
    console.log(`🚀 开始上传和转写片段 ${segmentIndex}`);

    const formData = new FormData();
    formData.append('segment', audioBlob, `segment_${segmentIndex}.webm`);
    formData.append('audio_id', audioId);
    formData.append('segment_index', segmentIndex.toString());
    formData.append('timestamp', new Date().toISOString());

    const response = await fetch(`${API_BASE_URL}/api/upload_and_transcribe`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `上传失败: ${response.status}`);
    }

    const result = await response.json();
    return result;
  };

  // 查询单个segment状态
  const querySegmentStatus = async (audioId, segmentIndex) => {
    const url = `${API_BASE_URL}/api/segment/${audioId}/${segmentIndex}/status`;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`查询失败: ${response.status}`);
    }

    return await response.json();
  };

  // 检查后端健康状态
  const checkHealthStatus = async () => {
    try {
      setHealthStatus('checking');
      setHealthError(null);

      const response = await fetch(`${API_BASE_URL}/api/health`);

      if (response.ok) {
        const healthData = await response.json();
        setHealthStatus('healthy');
        console.log('🟢 后端健康状态:', healthData);
      } else {
        setHealthStatus('unhealthy');
        setHealthError(`HTTP ${response.status}`);
      }
    } catch (error) {
      setHealthStatus('error');
      setHealthError(error.message);
      console.error('🔴 后端健康检查失败:', error);
    }
  };

  // 任务类
  class SegmentTask {
    constructor(audioBlob, audioId, segmentIndex, duration) {
      this.audioBlob = audioBlob;
      this.audioId = audioId;
      this.segmentIndex = segmentIndex;
      this.duration = duration;
      this.uploadAttempted = false;
      this.retryCount = 0;
      this.maxRetries = 1;
      this.lastError = null;
      this.nextRetryTime = null;
      this.status = SegmentStatus.PENDING;
      this.transcriptionText = null;
      this.transcriptionData = null;
      this.previousStatus = null;
    }

    async execute() {
      try {
        if (!this.uploadAttempted) {
          this.status = SegmentStatus.UPLOADING;
          this.updateUI();

          const result = await uploadAndTranscribe(
            this.audioBlob,
            this.audioId,
            this.segmentIndex,
            this.duration
          );

          this.uploadAttempted = true;

          if (result.transcription_text || result.transcription_data) {
            this.transcriptionText = result.transcription_text;
            this.transcriptionData = result.transcription_data;
          }

          if (failedTasksRef.current[this.segmentIndex]) {
            delete failedTasksRef.current[this.segmentIndex];
          }

          try {
            const statusResult = await querySegmentStatus(this.audioId, this.segmentIndex);

            if (statusResult.success && statusResult.transcription_file_exists) {
              this.status = SegmentStatus.COMPLETED;
              if (statusResult.transcription_data) {
                this.transcriptionText = statusResult.transcription_data.text;
                this.transcriptionData = statusResult.transcription_data;
              }
            } else if (statusResult.success && statusResult.audio_file_exists) {
              await this.transcribeOnly(statusResult.audio_file_url);
              const finalStatus = await querySegmentStatus(this.audioId, this.segmentIndex);
              if (finalStatus.success && finalStatus.transcription_file_exists) {
                this.status = SegmentStatus.COMPLETED;
                if (finalStatus.transcription_data) {
                  this.transcriptionText = finalStatus.transcription_data.text;
                  this.transcriptionData = finalStatus.transcription_data;
                }
              } else {
                this.status = SegmentStatus.FAILED;
              }
            } else {
              this.status = SegmentStatus.FAILED;
            }
          } catch (statusError) {
            console.error(`状态查询失败:`, statusError);
            this.status = result.transcription_completed ? SegmentStatus.COMPLETED : SegmentStatus.FAILED;
          }

          this.updateUI();
          return result;
        }
      } catch (error) {
        try {
          const statusResult = await querySegmentStatus(this.audioId, this.segmentIndex);
          if (statusResult.success && statusResult.transcription_file_exists) {
            this.status = SegmentStatus.COMPLETED;
          } else {
            this.status = SegmentStatus.FAILED;
          }
        } catch {
          this.status = SegmentStatus.FAILED;
        }

        if (this.status !== SegmentStatus.COMPLETED) {
          this.onError(error);
          throw error;
        }
      }
    }

    async transcribeOnly(audioFileUrl) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/transcribe_only`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            audio_id: this.audioId,
            segment_index: this.segmentIndex,
            audio_file_url: audioFileUrl
          })
        });

        if (!response.ok) {
          throw new Error(`转写失败: ${response.status}`);
        }

        return await response.json();
      } catch (error) {
        console.error(`仅转写失败:`, error);
        throw error;
      }
    }

    onError(error) {
      console.error(`片段 ${this.segmentIndex} 处理失败:`, error);
      this.lastError = error.message;
      this.status = SegmentStatus.FAILED;

      if (this.retryCount === 0) {
        this.retryCount++;
        const retryDelay = 3000;
        this.nextRetryTime = Date.now() + retryDelay;
        scheduleRetry(this);
      } else {
        console.error(`片段 ${this.segmentIndex} 达到最大重试次数`);
        if (retryTimersRef.current[this.segmentIndex]) {
          clearTimeout(retryTimersRef.current[this.segmentIndex]);
          delete retryTimersRef.current[this.segmentIndex];
        }
      }

      this.updateUI();
    }

    updateUI() {
      const statusChanged = this.previousStatus !== this.status;
      this.previousStatus = this.status;

      setSegments(prev => {
        const localAudioData = audioSegmentsRef.current.get(this.segmentIndex);

        return prev.map(seg =>
          seg.index === this.segmentIndex
            ? {
                ...seg,
                status: this.status,
                retryCount: this.retryCount,
                error: this.lastError,
                nextRetryTime: this.nextRetryTime,
                uploadAttempted: this.uploadAttempted,
                transcription: this.transcriptionData || seg.transcription,
                localAudioUrl: seg.localAudioUrl || localAudioData?.audioUrl || null,
                hasLocalAudio: seg.hasLocalAudio || !!localAudioData
              }
            : seg
        );
      });
    }
  }

  // 安排重试
  const scheduleRetry = (task) => {
    const retryDelay = task.nextRetryTime - Date.now();

    const timerId = setTimeout(async () => {
      if (task.retryCount === 1) {
        task.status = SegmentStatus.RETRYING;
        task.updateUI();

        try {
          await task.execute();
        } catch (err) {
          console.error(`重试失败:`, err);
        }
      }

      delete retryTimersRef.current[task.segmentIndex];
    }, retryDelay);

    retryTimersRef.current[task.segmentIndex] = timerId;
  };

  // 检查所有任务是否真正完成
  const checkAllTasksComplete = () => {
    // 检查队列是否为空
    const queueEmpty = taskQueueRef.current.length === 0;

    // 检查是否有重试定时器
    const noRetries = Object.keys(retryTimersRef.current).length === 0;

    // 检查所有segment是否都完成或失败（没有pending/uploading/transcribing/retrying状态）
    const allSegmentsSettled = segments.every(seg =>
      seg.status === SegmentStatus.COMPLETED || seg.status === SegmentStatus.FAILED
    );

    const isComplete = queueEmpty && noRetries && allSegmentsSettled;

    console.log('📊 任务完成状态检查:', {
      queueEmpty,
      noRetries,
      allSegmentsSettled,
      isComplete,
      queueLength: taskQueueRef.current.length,
      retryCount: Object.keys(retryTimersRef.current).length,
      segments: segments.map(s => ({ index: s.index, status: s.status }))
    });

    return isComplete;
  };

  // 处理任务队列
  const processQueue = async () => {
    if (isProcessingQueueRef.current || taskQueueRef.current.length === 0) {
      return;
    }

    isProcessingQueueRef.current = true;

    while (taskQueueRef.current.length > 0) {
      const task = taskQueueRef.current.shift();

      try {
        await task.execute();
      } catch (error) {
        console.error(`任务执行失败:`, error);
      }
    }

    isProcessingQueueRef.current = false;

    // 队列处理完后检查是否所有任务真正完成
    const allComplete = checkAllTasksComplete();
    setIsQueueEmpty(allComplete);
  };

  // 添加任务到队列
  const addTaskToQueue = (audioBlob, audioId, segmentIndex, duration) => {
    // 检查是否在取消录音，防止取消后仍然添加任务
    if (isCancellingRef.current) {
      console.log('🚫 录音已取消，跳过添加任务到队列');
      return;
    }

    const task = new SegmentTask(audioBlob, audioId, segmentIndex, duration);
    taskQueueRef.current.push(task);
    setIsQueueEmpty(false);
    processQueue();
  };

  // 加载历史会话
  const loadHistoricalSession = async (audioId) => {
    try {
      console.log('🔍 加载历史会话:', audioId);
      setIsRecordingStopped(true);
      setIsQueueEmpty(true);

      let hasD1Record = false;

      // 尝试从 D1 加载记录详情
      try {
        const recordData = await apiService.getRecord(audioId);
        console.log('📋 D1记录数据:', recordData);

        if (recordData.data) {
          hasD1Record = true;
          setRecordCreated(true);
          const record = recordData.data.record || recordData.data;

          console.log('📝 加载D1记录字段:', {
            transcriptionText: record.transcriptionText,
            polishInstruction: record.polishInstruction,
            polishedText: record.polishedText,
            fav: record.fav
          });

          // 设置转写文本（后端字段名是 transcriptionText）
          if (record.transcriptionText) {
            setTranscriptionText(record.transcriptionText);
            transcriptionTextRef.current = record.transcriptionText;
          }

          // 设置润色相关
          const instruction = record.polishInstruction || '';
          const polished = record.polishedText || '';

          console.log('🔧 设置润色状态:', { instruction, polished });

          setPolishInstruction(instruction);
          polishInstructionRef.current = instruction;

          setPolishedText(polished);
          polishedTextRef.current = polished;

          // 设置收藏状态
          if (record.fav !== undefined) {
            setIsFavorite(record.fav);
          }

          // 立即显示润色UI，无需等待R2数据加载
          setShowPolishUI(true);
          console.log('✅ 立即显示润色UI（从D1加载完成）');
        }
      } catch (d1Error) {
        console.warn('⚠️ D1中无此记录:', d1Error);
        // D1中没有记录，继续尝试加载R2数据
      }

      // 异步加载 R2 中的 segment 信息（不阻塞UI显示）
      setTimeout(async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/audio/${audioId}/status`);
          if (response.ok) {
            const audioStatus = await response.json();
            console.log('📋 R2音频状态（异步加载）:', audioStatus);

            if (audioStatus.segments && audioStatus.segments.length > 0) {
              const formattedSegments = audioStatus.segments.map(seg => ({
                index: seg.segment_index,
                fileName: `segment_${seg.segment_index}.webm`,
                size: 0,
                duration: uploadInterval, // 使用环境变量配置的分段时长
                status: seg.status === 'completed' ? SegmentStatus.COMPLETED :
                        seg.status === 'uploaded' ? SegmentStatus.UPLOADED :
                        SegmentStatus.FAILED,
                transcription: seg.transcription_data,
                retryCount: 0,
                maxRetries: 1,
                error: null,
                nextRetryTime: null,
                uploadAttempted: true,
                localAudioUrl: null,
                hasLocalAudio: false
              }));
              setSegments(formattedSegments);

              // 如果D1没有记录，但R2有数据，从R2收集转写文本
              if (!hasD1Record && formattedSegments.some(s => s.transcription)) {
                const r2Transcription = formattedSegments
                  .filter(s => s.transcription && s.transcription.text)
                  .map(s => s.transcription.text)
                  .join('\n\n');
                if (r2Transcription) {
                  setTranscriptionText(r2Transcription);
                  // 如果D1没有记录但R2有数据，显示润色UI
                  setShowPolishUI(true);
                  console.log('✅ 显示润色UI（从R2异步加载转写文本）');
                }
              }

              console.log('✅ R2 segment信息异步加载完成');
            } else {
              console.log('📋 R2中没有segment数据');
            }
          } else {
            console.warn('⚠️ 无法获取R2音频状态');
          }
        } catch (segmentError) {
          console.error('❌ R2 segment信息异步加载失败:', segmentError);
          // 如果R2加载失败，且D1也没有记录，设置错误
          if (!hasD1Record) {
            setError('无法加载录音数据: ' + segmentError.message);
          }
        }
      }, 100); // 延迟100ms异步加载，确保UI先显示

    } catch (error) {
      console.error('❌ 加载历史会话失败:', error);
      setError('加载历史会话失败: ' + error.message);
    }
  };

  // URL参数解析
  useEffect(() => {
    if (urlAudioId) {
      console.log('🔗 检测到URL中的会话ID:', urlAudioId);
      audioIdRef.current = urlAudioId;

      // 如果是正在进行的新录音导致的URL变化，不加载历史会话
      if (!isNewRecordingRef.current) {
        // 加载历史会话
        loadHistoricalSession(urlAudioId);
      } else {
        console.log('⏭️ 跳过历史会话加载（正在进行新录音）');
      }
    } else {
      // 没有URL参数，说明是录音准备页面，需要重置状态
      console.log('🔄 没有URL参数，重置为录音准备页面状态');

      // 重置所有状态
      setError('');
      setSegments([]);
      setRecordingTime(0);
      segmentIndexRef.current = 1;
      audioChunksRef.current = [];
      audioSegmentsRef.current = new Map();
      taskQueueRef.current = [];
      setIsRecordingStopped(false);
      setIsQueueEmpty(false);
      setShowPolishUI(false);
      setRecordCreated(false);
      setRecordCreateError(null);
      setTranscriptionText('');
      setPolishedText('');
      setPolishInstruction('');
      setHasStartedRecording(false);
      isNewRecordingRef.current = false;
      audioIdRef.current = null;

      // 清理重试定时器
      Object.values(retryTimersRef.current).forEach(timerId => {
        clearTimeout(timerId);
      });
      retryTimersRef.current = {};

      if (autoStart) {
        // 自动开始录音
        setTimeout(() => {
          startRecording();
        }, 500);
      }
    }
  }, [urlAudioId, autoStart]);

  // 健康状态检查 - 只在页面加载时检查一次
  useEffect(() => {
    checkHealthStatus();
  }, []); // 移除依赖项，只在组件挂载时执行一次

  // 监听segment状态变化，定期检查任务是否全部完成
  useEffect(() => {
    if (isRecordingStopped && !isQueueEmpty) {
      // 每秒检查一次任务是否完成
      const checkInterval = setInterval(() => {
        const allComplete = checkAllTasksComplete();
        if (allComplete) {
          setIsQueueEmpty(true);
          clearInterval(checkInterval);
        }
      }, 1000);

      return () => clearInterval(checkInterval);
    }
  }, [isRecordingStopped, segments, isQueueEmpty]);

  // 监听队列状态，队列清空后显示润色UI并自动触发润色
  useEffect(() => {
    // 必须是录音完成后（不是历史会话加载），且队列清空，且记录创建成功
    if (isRecordingStopped && isQueueEmpty && recordCreated && !recordCreateError && !showPolishUI && hasStartedRecording) {
      console.log('✅ 所有任务完成，显示润色UI并自动开始润色');
      setShowPolishUI(true);

      // 收集所有转写文本
      const fullTranscription = collectTranscriptionText();

      // 将完整的转写文本保存到远程数据库
      if (fullTranscription && audioIdRef.current) {
        apiService.updateRecord(audioIdRef.current, {
          transcriptionText: fullTranscription  // 后端字段名是 transcriptionText
        }).then(() => {
          console.log('✅ 转写文本已保存到数据库');
        }).catch(error => {
          console.error('❌ 转写文本保存失败:', error);
        });
      }

      // 自动触发润色（只有在有转写内容时）
      if (fullTranscription) {
        setTimeout(() => {
          handlePolish(true); // 使用默认指令进行自动润色
        }, 500); // 延迟500ms确保UI已显示
      }
    }
  }, [isRecordingStopped, isQueueEmpty, recordCreated, recordCreateError, showPolishUI, hasStartedRecording]);

  // 收集所有转写文本
  const collectTranscriptionText = () => {
    // 只收集已完成的segment
    const completedSegments = segments.filter(seg =>
      seg.status === SegmentStatus.COMPLETED &&
      seg.transcription &&
      seg.transcription.text
    );

    const allText = completedSegments
      .map(seg => seg.transcription.text)
      .join('\n\n'); // 用空行分隔不同的片段

    setTranscriptionText(allText);
    console.log(`📝 收集到的转写文本 (${completedSegments.length}/${segments.length}个片段):`, allText);

    return allText; // 返回文本供其他函数使用
  };

  // 开始录音
  const startRecording = async () => {
    try {
      // 清除所有状态
      setError('');
      setSegments([]);
      setRecordingTime(0);
      segmentIndexRef.current = 1;
      audioChunksRef.current = [];
      audioSegmentsRef.current = new Map();
      taskQueueRef.current = [];
      setIsRecordingStopped(false);
      setIsQueueEmpty(false);
      setShowPolishUI(false);
      setRecordCreated(false);
      setRecordCreateError(null);
      setTranscriptionText('');
      setPolishedText('');
      setPolishInstruction('');

      setIsStarting(true);

      const newAudioId = generateAudioId();
      audioIdRef.current = newAudioId;

      // 标记为新录音，防止加载历史会话
      isNewRecordingRef.current = true;

      // 立即跳转到新的URL
      navigate(`/recording/${newAudioId}`, { replace: true });

      // 检测浏览器类型和设备，优化录音配置
      const isMacChrome = /Mac/.test(navigator.userAgent) && /Chrome/.test(navigator.userAgent);
      const isChrome = /Chrome/.test(navigator.userAgent);
      console.log(`🔍 检测到浏览器: ${isMacChrome ? 'Mac Chrome' : isChrome ? 'Chrome' : '其他'}`);

      // 根据浏览器和设备调整音频约束
      let audioConstraints;

      if (isMacChrome) {
        console.log('🔧 使用Mac Chrome优化的音频配置');
        // 对于Mac Chrome，禁用可能影响蓝牙设备的音频处理特性
        audioConstraints = {
          deviceId: selectedDeviceId ? { exact: selectedDeviceId } : undefined,
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
          sampleRate: 48000,
          channelCount: 1,
          latency: 0.01,
          googEchoCancellation: false,
          googNoiseSuppression: false,
          googAutoGainControl: false
        };
      } else {
        console.log('🔧 使用标准音频配置');
        audioConstraints = {
          deviceId: selectedDeviceId ? { exact: selectedDeviceId } : undefined,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100,
          channelCount: 1
        };
      }

      // 获取麦克风权限
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints
      });

      console.log('🎤 麦克风权限获取成功');

      // 获取并显示音频轨道信息
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        const settings = audioTrack.getSettings();
        console.log(`📊 音频轨道信息:`, settings);
        console.log(`🎤 当前设备: ${settings.deviceId || '未知'}`);
        console.log(`🔊 采样率: ${settings.sampleRate}Hz`);
        console.log(`📢 声道数: ${settings.channelCount}`);

        // 检查设备标签，帮助调试
        const deviceLabel = audioTrack.label || '未知设备';
        console.log(`🏷️ 设备标签: ${deviceLabel}`);

        // 如果是AirPods，给出特别提示
        if (deviceLabel.toLowerCase().includes('airpods') || deviceLabel.toLowerCase().includes('air')) {
          console.log('🎧 检测到AirPods设备，使用优化配置');
        }
      }

      streamRef.current = stream;

      // 检查支持的MIME类型 - 优先考虑Chrome兼容性
      const supportedTypes = [
        'audio/webm',
        'audio/webm;codecs=opus',
        'audio/mp4',
        'audio/ogg'
      ];

      let finalMimeType = 'audio/webm;codecs=opus';
      if (MediaRecorder.isTypeSupported(finalMimeType)) {
        console.log(`✅ 支持的MIME类型: ${finalMimeType}`);
      } else {
        // 找到第一个支持的类型
        for (const type of supportedTypes) {
          if (MediaRecorder.isTypeSupported(type)) {
            finalMimeType = type;
            console.log(`🔄 切换到支持的MIME类型: ${type}`);
            break;
          }
        }
      }

      // 创建MediaRecorder - 针对不同浏览器优化
      let mediaRecorder;
      try {
        const recorderOptions = {
          mimeType: finalMimeType
        };

        // 对于Chrome，添加更多选项以提高兼容性
        if (isChrome) {
          recorderOptions.audioBitsPerSecond = 128000; // 128kbps
        }

        mediaRecorder = new MediaRecorder(stream, recorderOptions);
      } catch (error) {
        console.error('❌ MediaRecorder创建失败，尝试不使用MIME类型:', error);
        // 如果失败，尝试不指定MIME类型
        try {
          mediaRecorder = new MediaRecorder(stream);
          finalMimeType = mediaRecorder.mimeType || 'audio/webm';
        } catch (secondError) {
          console.error('❌ MediaRecorder创建完全失败:', secondError);
          throw new Error('无法创建MediaRecorder，请检查浏览器兼容性');
        }
      }
      mediaRecorderRef.current = mediaRecorder;

      console.log(`🎯 MediaRecorder创建成功，使用MIME类型: ${finalMimeType}`);

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        createAudioSegment();
      };

      // 立即开始录音
      audioChunksRef.current = [];
      isRecordingRef.current = true;
      const now = Date.now();
      recordingStartTimeRef.current = now;
      segmentStartTimeRef.current = now;

      mediaRecorder.start();
      setIsRecording(true);
      setIsStarting(false);
      setHasStartedRecording(true); // 标记已开始录音

      // 启动定时器
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      // 启动切分定时器
      startSplitTimer();

    } catch (err) {
      console.error('录音启动失败:', err);
      setError('无法访问麦克风，请检查权限设置');
      setIsStarting(false);
    }
  };

  // 启动切分定时器
  const startSplitTimer = () => {
    splitTimerRef.current = setInterval(() => {
      if (isRecordingRef.current && mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();

        setTimeout(() => {
          if (isRecordingRef.current && mediaRecorderRef.current) {
            mediaRecorderRef.current.ondataavailable = (event) => {
              if (event.data.size > 0) {
                audioChunksRef.current.push(event.data);
              }
            };

            mediaRecorderRef.current.onstop = () => {
              createAudioSegment();
            };

            mediaRecorderRef.current.start();
            segmentStartTimeRef.current = Date.now();
          }
        }, 100);
      }
    }, uploadInterval * 1000);
  };

  // 创建音频片段
  const createAudioSegment = () => {
    // 检查是否在取消录音，防止取消后仍然创建片段
    if (isCancellingRef.current || audioChunksRef.current.length === 0) {
      return;
    }

    const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm;codecs=opus' });
    const audioUrl = URL.createObjectURL(audioBlob);
    const duration = (Date.now() - segmentStartTimeRef.current) / 1000;

    const segmentData = {
      audioBlob,
      audioUrl,
      segmentIndex: segmentIndexRef.current,
      duration,
      timestamp: new Date().toISOString()
    };

    audioSegmentsRef.current.set(segmentIndexRef.current, segmentData);

    const newSegment = {
      index: segmentIndexRef.current,
      fileName: `segment_${segmentIndexRef.current}.webm`,
      size: audioBlob.size,
      duration: duration,
      status: SegmentStatus.PENDING,
      retryCount: 0,
      maxRetries: 1,
      error: null,
      nextRetryTime: null,
      uploadAttempted: false,
      localAudioUrl: audioUrl,
      hasLocalAudio: true
    };

    setSegments(prev => [...prev, newSegment]);

    addTaskToQueue(audioBlob, audioIdRef.current, segmentIndexRef.current, duration);

    audioChunksRef.current = [];
    segmentIndexRef.current++;
  };

  // 取消录音
  const cancelRecording = async () => {
    console.log('取消录音');

    // 设置取消标志，防止创建新的音频片段
    isCancellingRef.current = true;
    isRecordingRef.current = false;

    // 停止切分定时器
    if (splitTimerRef.current) {
      clearInterval(splitTimerRef.current);
      splitTimerRef.current = null;
    }

    // 停止录音器（先清除事件监听器，防止onstop触发）
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      // 清除事件监听器，防止onstop触发createAudioSegment
      mediaRecorderRef.current.ondataavailable = null;
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.stop();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // 重置所有状态
    isRecordingRef.current = false;
    setIsRecording(false);
    setIsPaused(false);
    setIsRecordingStopped(false);
    setRecordingTime(0);
    setSegments([]);
    setTranscriptionText('');
    setPolishInstruction('');
    setPolishedText('');
    setShowPolishUI(false);
    setError('');
    setRecordCreated(false);
    setRecordCreateError(null);
    setHasStartedRecording(false);

    // 清空任务队列
    taskQueueRef.current = [];
    isProcessingQueueRef.current = false;

    // 清空音频缓存
    audioSegmentsRef.current.clear();

    // 清理重试定时器
    Object.values(retryTimersRef.current).forEach(timerId => {
      clearTimeout(timerId);
    });
    retryTimersRef.current = {};

    // 重置audioId
    audioIdRef.current = null;

    // 重置取消标志
    isCancellingRef.current = false;

    console.log('✅ 录音已取消，返回初始状态');
  };

  // 暂停录音
  const pauseRecording = async () => {
    if (splitTimerRef.current) {
      clearInterval(splitTimerRef.current);
      splitTimerRef.current = null;
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    setIsPaused(true);
  };

  // 继续录音
  const resumeRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'inactive') {
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        createAudioSegment();
      };

      mediaRecorderRef.current.start();
      segmentStartTimeRef.current = Date.now();
    }

    timerRef.current = setInterval(() => {
      setRecordingTime(prev => prev + 1);
    }, 1000);

    startSplitTimer();
    setIsPaused(false);
  };

  // 停止录音
  const stopRecording = async () => {
    console.log('🛑 开始停止录音流程...');
    setIsStopping(true);

    const finalAudioId = audioIdRef.current;

    // 停止切分定时器
    if (splitTimerRef.current) {
      clearInterval(splitTimerRef.current);
      splitTimerRef.current = null;
    }

    // 停止录音器
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    isRecordingRef.current = false;
    setIsRecording(false);
    setIsPaused(false);
    setIsRecordingStopped(true);
    isNewRecordingRef.current = false; // 录音结束，清除新录音标记

    // 同步地POST新的音频记录
    try {
      console.log('📤 提交新的音频记录...');
      await apiService.createRecord(finalAudioId, recordingTime);
      setRecordCreated(true);
      setRecordCreateError(null);
      console.log('✅ 音频记录创建成功');
    } catch (error) {
      console.error('❌ 音频记录创建失败:', error);
      setRecordCreateError(error.message);
      setRecordCreated(false);
    }

    // 等待所有任务完成
    console.log('⏳ 等待所有任务完成...');
    await waitForAllTasksComplete();

    setIsStopping(false);
  };

  // 等待所有任务完成
  const waitForAllTasksComplete = async () => {
    let attempts = 0;
    const maxAttempts = 30;

    while (attempts < maxAttempts) {
      const queueEmpty = taskQueueRef.current.length === 0;
      const noRetries = Object.keys(retryTimersRef.current).length === 0;

      if (queueEmpty && noRetries) {
        console.log('✅ 所有任务已完成');
        return;
      }

      await new Promise(resolve => setTimeout(resolve, 1000));
      attempts++;
    }

    console.warn('⚠️ 强制完成等待');
  };

  // 润色文本
  const handlePolish = async (useDefaultInstruction = false) => {
    // 根据润色模式选择输入源
    let textToPolish;
    if (polishMode === 'polished') {
      // 基于润色结果模式
      textToPolish = polishedText;
      if (!textToPolish || textToPolish.trim() === '') {
        setError('没有可润色的润色文本，请先进行一次润色或切换到基于原始转写模式');
        return;
      }
    } else {
      // 基于原始转写模式（默认）
      textToPolish = transcriptionText;
      if (!textToPolish) {
        textToPolish = collectTranscriptionText();
      }
      if (!textToPolish || textToPolish.trim() === '') {
        console.warn('⚠️ 没有可润色的文本，跳过自动润色');
        setError('没有可润色的转写文本');
        return;
      }
    }

    try {
      // 如果是自动润色（录音完成后的第一次润色），不使用指令
      // 如果是手动润色，检查用户是否输入了指令
      let instruction;
      if (useDefaultInstruction) {
        instruction = '';
        console.log('🎨 开始自动润色（无指令）...');
      } else {
        if (!polishInstruction || polishInstruction.trim() === '') {
          setError('请输入润色指令后再进行润色');
          return;
        }
        instruction = polishInstruction;
        console.log('🎨 开始手动润色...');
      }

      setIsPolishing(true);
      setError('');

      const result = await apiService.polishText(textToPolish, instruction);

      if (result.polishedText) {
        setPolishedText(result.polishedText);
        polishedTextRef.current = result.polishedText; // 更新ref
        polishInstructionRef.current = instruction; // 更新ref

        // 如果是自动润色，不更新指令状态（保持为空）

        console.log('✅ 润色完成');

        // 保存润色结果到记录
        await apiService.updateRecord(audioIdRef.current, {
          polishedText: result.polishedText,
          polishInstruction: instruction
        });
      }
    } catch (error) {
      console.error('润色失败:', error);
      setError('润色失败: ' + error.message);
    } finally {
      setIsPolishing(false);
    }
  };

  // 重新润色
  const handleRepolish = async () => {
    setPolishedText('');
    await handlePolish();
  };

  // 复制润色结果
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(polishedText || transcriptionText);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('复制失败:', err);
    }
  };

  // 删除记录
  const handleDelete = async () => {
    if (!window.confirm('确定要删除这条记录吗？')) {
      return;
    }

    try {
      const currentAudioId = audioIdRef.current;

      // 获取下一条记录（时间顺序上的上一条）
      let nextAudioId = null;
      try {
        // 删除后应该跳转到时间更早的记录，也就是prev
        const prevResponse = await apiService.getPreviousRecord(currentAudioId);
        if (prevResponse.success && prevResponse.data?.audioId) {
          nextAudioId = prevResponse.data.audioId;
          console.log(`📋 找到时间更早的记录: ${nextAudioId}`);
        } else {
          // 如果没有更早的记录，则跳转到时间更近的记录（next）
          const nextResponse = await apiService.getNextRecord(currentAudioId);
          if (nextResponse.success && nextResponse.data?.audioId) {
            nextAudioId = nextResponse.data.audioId;
            console.log(`📋 没有更早的记录，跳转到时间更近的记录: ${nextAudioId}`);
          }
        }
      } catch (error) {
        console.warn('获取下一条记录失败:', error);
      }

      // 删除当前记录
      await apiService.deleteRecord(currentAudioId);
      console.log(`✅ 记录 ${currentAudioId} 删除成功`);

      // 跳转逻辑
      if (nextAudioId) {
        // 跳转到下一条记录
        navigate(`/recording/${nextAudioId}`, {
          replace: true,
          state: {
            from: 'delete',
            deletedAudioId: currentAudioId
          }
        });
        console.log(`🔄 自动跳转到下一条记录: ${nextAudioId}`);
      } else {
        // 没有下一条记录，返回录音准备页面
        navigate('/recording', {
          replace: true,
          state: {
            from: 'delete',
            deletedAudioId: currentAudioId
          }
        });
        console.log(`🔄 没有更多记录，返回录音准备页面`);
      }
    } catch (error) {
      console.error('删除失败:', error);
      setError('删除失败: ' + error.message);
    }
  };

  // 返回处理
  const handleBack = () => {
    console.log('🔙 处理返回逻辑');
    console.log('📍 来源信息:', {
      from: location.state?.from,
      hasUrlAudioId: !!urlAudioId,
      hasStartedRecording,
      isNewRecording: isNewRecordingRef.current
    });

    // 判断返回逻辑
    if (location.state?.from === 'list' && location.state?.clickedIndex !== undefined) {
      // 从列表页进入，返回到列表页并保持状态
      console.log('📋 返回到列表页');
      navigate('/records', {
        state: {
          from: 'detail',
          clickedIndex: location.state.clickedIndex,
          scrollPosition: location.state.scrollPosition
        }
      });
    } else {
      // 新录音或直接访问，返回到录音准备页面
      console.log('🎤 返回到录音准备页面');
      navigate('/recording');
    }
  };

  // 收藏/取消收藏
  const handleToggleFavorite = async () => {
    try {
      // 后端字段名是 fav，不是 isFavorite
      await apiService.updateRecord(audioIdRef.current, {
        fav: !isFavorite
      });
      setIsFavorite(!isFavorite);
    } catch (error) {
      console.error('收藏操作失败:', error);
      setError('收藏操作失败: ' + error.message);
    }
  };

  // 通用的字段更新处理函数（参考DetailPage）
  const handleFieldUpdate = async (fieldConfig) => {
    const {
      currentValue,    // 当前值
      ref,             // 对应的ref
      apiField,        // API发送时的字段名
      successMessage   // 成功消息
    } = fieldConfig;

    // 只有当值发生变化时才保存
    if (currentValue !== ref.current && audioIdRef.current && recordCreated) {
      try {
        const updateData = {};
        updateData[apiField] = currentValue;

        await apiService.updateRecord(audioIdRef.current, updateData);
        ref.current = currentValue; // 更新ref为新值
        console.log(`✅ ${apiField} 已保存`);

        // 显示保存成功反馈
        if (successMessage) {
          showSaveSuccessMessage(successMessage);
        }
      } catch (error) {
        console.error(`❌ 保存${apiField}失败:`, error);
      }
    }
  };

  // 润色指令失去焦点时保存
  const handlePolishInstructionBlur = () => {
    handleFieldUpdate({
      currentValue: polishInstruction,
      ref: polishInstructionRef,
      apiField: 'polishInstruction',
      successMessage: '指令已保存'
    });
  };

  // 润色文本失去焦点时保存
  const handlePolishedTextBlur = () => {
    handleFieldUpdate({
      currentValue: polishedText,
      ref: polishedTextRef,
      apiField: 'polishedText',
      successMessage: '润色文本已保存'
    });
  };

  // 获取片段状态图标
  const getSegmentStatusIcon = (segment) => {
    switch (segment.status) {
      case SegmentStatus.COMPLETED:
        return '✅';
      case SegmentStatus.UPLOADING:
      case SegmentStatus.TRANSCRIBING:
      case SegmentStatus.RETRYING:
        return '🔄';
      case SegmentStatus.FAILED:
        return '❌';
      default:
        return '⏳';
    }
  };

  // 获取片段状态文本
  const getSegmentStatusText = (segment) => {
    switch (segment.status) {
      case SegmentStatus.COMPLETED:
        return '转写完成';
      case SegmentStatus.UPLOADING:
        return '上传中...';
      case SegmentStatus.TRANSCRIBING:
        return '转写中...';
      case SegmentStatus.RETRYING:
        return `重试中 (${segment.retryCount}/${segment.maxRetries})`;
      case SegmentStatus.FAILED:
        return `失败: ${segment.error || '未知错误'}`;
      default:
        return '等待处理';
    }
  };

  // 清理资源
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (splitTimerRef.current) {
        clearInterval(splitTimerRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      audioSegmentsRef.current.forEach((segmentData) => {
        URL.revokeObjectURL(segmentData.audioUrl);
      });
      audioSegmentsRef.current = new Map();

      Object.values(retryTimersRef.current).forEach(timerId => {
        clearTimeout(timerId);
      });
    };
  }, []);

  // 自动调整textarea高度的useEffect
  useEffect(() => {
    // 初始化时调整高度
    if (polishedTextareaRef.current) {
      autoResizeTextarea(polishedTextareaRef.current);
    }
    if (instructionTextareaRef.current) {
      autoResizeTextarea(instructionTextareaRef.current);
    }
  }, [autoResizeTextarea]);

  // 监听文本变化，自动调整高度
  useEffect(() => {
    if (polishedTextareaRef.current) {
      autoResizeTextarea(polishedTextareaRef.current);
    }
  }, [polishedText, autoResizeTextarea]);

  useEffect(() => {
    if (instructionTextareaRef.current) {
      autoResizeTextarea(instructionTextareaRef.current);
    }
  }, [polishInstruction, autoResizeTextarea]);

  // 跳转到下一条记录（时间更近的）
  const navigateToNextRecord = useCallback(async (currentAudioId) => {
    try {
      console.log(`⬆️ 查找下一条记录，当前记录: ${currentAudioId}`);

      const response = await apiService.getPreviousRecord(currentAudioId);

      if (response.success && response.data?.audioId) {
        const nextAudioId = response.data.audioId;
        console.log(`⬆️ 快捷键跳转到下一条记录: ${nextAudioId}`);
        navigate(`/recording/${nextAudioId}`, {
          replace: true,
          state: {
            from: 'keyboard_navigation',
            direction: 'next'
          }
        });
      } else {
        console.log('⬆️ 已经是最新记录，无法跳转');
      }
    } catch (error) {
      console.error('跳转到下一条记录失败:', error);
    }
  }, []);

  // 跳转到上一条记录（时间更早的）
  const navigateToPreviousRecord = useCallback(async (currentAudioId) => {
    try {
      console.log(`⬇️ 查找上一条记录，当前记录: ${currentAudioId}`);

      const response = await apiService.getNextRecord(currentAudioId);

      if (response.success && response.data?.audioId) {
        const previousAudioId = response.data.audioId;
        console.log(`⬇️ 快捷键跳转到上一条记录: ${previousAudioId}`);
        navigate(`/recording/${previousAudioId}`, {
          replace: true,
          state: {
            from: 'keyboard_navigation',
            direction: 'previous'
          }
        });
      } else {
        console.log('⬇️ 已经是最早记录，无法跳转');
      }
    } catch (error) {
      console.error('跳转到上一条记录失败:', error);
    }
  }, []);

  // 快捷键功能 - 上键和下键切换记录
  useEffect(() => {
    const handleKeyDown = async (event) => {
      // 只在非输入状态下响应快捷键
      const activeElement = document.activeElement;
      const isInputFocused = activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.contentEditable === 'true'
      );

      // 如果正在输入，不响应快捷键
      if (isInputFocused) {
        return;
      }

      // 只在录音完成状态下（显示润色UI时）响应快捷键
      if (!showPolishUI || !audioIdRef.current) {
        return;
      }

      const currentAudioId = audioIdRef.current;

      // 上键：跳转到下一条记录（时间更近的）
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        await navigateToNextRecord(currentAudioId);
      }
      // 下键：跳转到上一条记录（时间更早的）
      else if (event.key === 'ArrowDown') {
        event.preventDefault();
        await navigateToPreviousRecord(currentAudioId);
      }
    };

    // 添加全局键盘事件监听器
    document.addEventListener('keydown', handleKeyDown);

    // 清理函数
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showPolishUI, navigateToNextRecord, navigateToPreviousRecord]);

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <div className={`flex-1 container mx-auto ${LAYOUT_CONFIG.PAGE_PADDING} ${LAYOUT_CONFIG.PAGE_SPACING} ${LAYOUT_CONFIG.PAGE_MAX_WIDTH} flex flex-col justify-center ${LAYOUT_CONFIG.BOTTOM_NAVIGATION_PADDING}`}>
        
        {/* 错误提示 */}
        {error && (
          <div className={`w-full ${LAYOUT_CONFIG.CONTENT_MAX_WIDTH} mx-auto mb-6 p-4 bg-red-900 bg-opacity-50 border border-red-600 rounded-lg`}>
            <div className="text-red-200 text-center text-sm">{error}</div>
          </div>
        )}

        {/* 记录创建失败提示 */}
        {recordCreateError && (
          <div className={`w-full ${LAYOUT_CONFIG.CONTENT_MAX_WIDTH} mx-auto mb-6 p-4 bg-red-900 bg-opacity-50 border border-red-600 rounded-lg`}>
            <div className="text-red-200 text-center text-sm">
              记录创建失败: {recordCreateError}
              <br />
              无法显示润色功能
            </div>
          </div>
        )}

        {/* 保存成功提示 - 固定在页面顶部中央 */}
        {showSaveSuccess && (
          <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300 ease-in-out">
            <div className="bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg transform transition-all duration-300 ease-in-out">
              {saveMessage}
            </div>
          </div>
        )}

        {/* 主要内容 - 居中显示 */}
        <div className={`w-full ${LAYOUT_CONFIG.CONTENT_MAX_WIDTH} mx-auto space-y-6 flex-1 flex flex-col justify-center`}>

          {/* 润色区域 - 录音完成后显示在最顶部 */}
          {showPolishUI && (
            <div className="space-y-6 w-full">
              {/* 润色指令 */}
              <div className="bg-gray-900 rounded-lg p-4 w-full">
                <h2 className="text-lg font-bold mb-3">润色指令</h2>

                <textarea
                  ref={instructionTextareaRef}
                  value={polishInstruction || ''}
                  onChange={(e) => {
                    setPolishInstruction(e.target.value);
                    autoResizeTextarea(e.target);
                  }}
                  onBlur={handlePolishInstructionBlur}
                  className="w-full bg-gray-800 text-white rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y min-h-12"
                  placeholder="请输入润色指令，例如：请将这段内容进行润色，使其更加通顺流畅"
                />

                {/* 润色模式开关 - 文本框下方 */}
                <div className="flex items-center justify-end mt-3">
                  <div className="flex items-center space-x-2 bg-gray-800 rounded-lg px-3 py-1.5">
                    <span className={`text-xs ${polishMode === 'original' ? 'text-blue-400' : 'text-gray-500'}`}>
                      基于原始结果
                    </span>
                    <button
                      onClick={() => setPolishMode(polishMode === 'original' ? 'polished' : 'original')}
                      className="relative inline-flex h-4 w-7 items-center rounded-full transition-colors focus:outline-none focus:ring-1 focus:ring-blue-500"
                      style={{ backgroundColor: polishMode === 'original' ? '#3B82F6' : '#6B7280' }}
                      title={polishMode === 'original' ? '基于原始转写结果润色' : '基于润色结果润色'}
                    >
                      <span
                        className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${polishMode === 'original' ? 'translate-x-0.5' : 'translate-x-4'}`}
                      />
                    </button>
                    <span className={`text-xs ${polishMode === 'polished' ? 'text-blue-400' : 'text-gray-500'}`}>
                      基于当前润色文本
                    </span>
                  </div>
                </div>
              </div>

              {/* 润色文本 */}
              <div className="bg-gray-900 rounded-lg p-6 w-full">
                <h2 className="text-lg font-bold mb-4">润色文本</h2>
                <textarea
                  ref={polishedTextareaRef}
                  value={polishedText || ''}
                  onChange={(e) => {
                    setPolishedText(e.target.value);
                    autoResizeTextarea(e.target);
                  }}
                  onBlur={handlePolishedTextBlur}
                  className="w-full bg-gray-800 text-white rounded-lg p-4 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y min-h-12"
                  placeholder="润色后的文本将显示在这里，或者您可以直接编辑..."
                />
              </div>

              {/* 操作按钮 */}
              <div className="bg-gray-900 rounded-lg p-6 w-full">
                <div className="flex flex-wrap gap-4 justify-center mb-4">
                  <button
                    onClick={handleBack}
                    className="px-6 py-3 bg-gray-600 rounded-full text-white font-medium hover:bg-gray-700 transition-colors"
                  >
                    ← 返回
                  </button>
                  <button
                    onClick={handleRepolish}
                    disabled={isPolishing}
                    className="px-6 py-3 bg-blue-600 rounded-full text-white font-medium hover:bg-blue-700 transition-colors disabled:bg-gray-600"
                  >
                    {isPolishing ? '润色中...' : '重新润色'}
                  </button>
                  <button
                    onClick={handleCopy}
                    className="px-6 py-3 bg-green-600 rounded-full text-white font-medium hover:bg-green-700 transition-colors"
                  >
                    {copySuccess ? '✓ 已复制' : '复制'}
                  </button>
                  <button
                    onClick={handleToggleFavorite}
                    className={`px-6 py-3 rounded-full text-white font-medium transition-colors ${
                      isFavorite ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-gray-600 hover:bg-gray-700'
                    }`}
                  >
                    {isFavorite ? '★ 已收藏' : '☆ 收藏'}
                  </button>
                  <button
                    onClick={handleDelete}
                    className="px-6 py-3 bg-red-600 rounded-full text-white font-medium hover:bg-red-700 transition-colors"
                  >
                    删除
                  </button>
                </div>

                </div>

              {/* 转写文本显示 */}
              <div className="bg-gray-900 rounded-lg p-6 w-full">
                <h2 className="text-lg font-bold mb-4">转写文本</h2>
                <div className="text-sm text-gray-400 mb-2">
                  这是从 {segments.filter(s => s.status === SegmentStatus.COMPLETED).length}/{segments.length} 个片段收集到的转写内容
                </div>
                <div className="w-full max-h-48 overflow-y-auto bg-gray-800 text-white rounded-lg p-4">
                  <pre className="whitespace-pre-wrap text-sm">{transcriptionText || '没有转写内容'}</pre>
                </div>
              </div>
            </div>
          )}

          {/* 录音控制 - 仅在未停止录音时显示 */}
          {!isRecordingStopped && (
            <div className="bg-gray-900 rounded-lg p-6 text-center w-full">
              {/* 问候语 - 仅在准备录音时显示 */}
              {!isRecording && !isStarting && !isStopping && user && (
                <div className="mb-6 text-gray-400">
                  你好，{user.name || '用户'}
                </div>
              )}

              {/* 设备选择器 - 仅在准备录音时显示 */}
              {!isRecording && !isStarting && !isStopping && (
                <div className="mb-6">
                  <div className="text-sm text-gray-300 font-medium mb-2">录音设备选择</div>
                  <div className="flex items-center space-x-2">
                    <select
                      value={selectedDeviceId}
                      onChange={(e) => selectDevice(e.target.value)}
                      disabled={isLoadingDevices}
                      className="flex-1 bg-gray-800 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      {availableDevices.map((device) => (
                        <option key={device.deviceId} value={device.deviceId}>
                          {device.deviceId === 'default'
                            ? `系统默认 - ${device.label || '默认设备'}`
                            : device.label || `设备 ${device.deviceId.slice(0, 8)}...`
                          }
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={getAudioDevices}
                      disabled={isLoadingDevices}
                      className="px-3 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="刷新设备列表"
                    >
                      {isLoadingDevices ? '🔄' : '🔄'}
                    </button>
                  </div>
                </div>
              )}

              {!isRecording && !isStarting && !isStopping ? (
                <button
                  onClick={startRecording}
                  disabled={isLoadingDevices}
                  className="px-8 py-4 bg-green-600 rounded-full text-white font-medium hover:bg-green-700 transition-colors disabled:bg-gray-600 disabled:cursor-not-allowed"
                >
                  🎤 开始录音
                </button>
              ) : isStarting ? (
                <div className="space-y-4">
                  <div className="text-2xl font-mono">启动中...</div>
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
                  </div>
                </div>
              ) : isStopping ? (
                <div className="space-y-4">
                  <div className="text-2xl font-mono">停止中...</div>
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
                  </div>
                  <div className="text-sm text-gray-400">正在等待所有任务完成</div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-2xl font-mono">{formatTime(recordingTime)}</div>
                  <div className="flex justify-center space-x-4">
                    {!isPaused ? (
                      <>
                        <button
                          onClick={cancelRecording}
                          className="px-6 py-3 bg-gray-600 rounded-full text-white font-medium hover:bg-gray-700 transition-colors"
                        >
                          取消
                        </button>
                        <button
                          onClick={pauseRecording}
                          className="px-6 py-3 bg-yellow-600 rounded-full text-white font-medium hover:bg-yellow-700 transition-colors"
                        >
                          暂停
                        </button>
                        <button
                          onClick={stopRecording}
                          className="px-6 py-3 bg-red-600 rounded-full text-white font-medium hover:bg-red-700 transition-colors"
                        >
                          完成
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={cancelRecording}
                          className="px-6 py-3 bg-gray-600 rounded-full text-white font-medium hover:bg-gray-700 transition-colors"
                        >
                          取消
                        </button>
                        <button
                          onClick={resumeRecording}
                          className="px-6 py-3 bg-blue-600 rounded-full text-white font-medium hover:bg-blue-700 transition-colors"
                        >
                          继续
                        </button>
                        <button
                          onClick={stopRecording}
                          className="px-6 py-3 bg-red-600 rounded-full text-white font-medium hover:bg-red-700 transition-colors"
                        >
                          ⏹️ 完成
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 录音片段列表 */}
          {segments.length > 0 && (
            <div className="bg-gray-900 rounded-lg p-6 w-full">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold">录音片段</h2>
                <div className="text-sm text-gray-400">
                  共 {segments.length} 个片段
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 w-full">
                {segments.map((segment, index) => (
                  <div key={index} className={`bg-gray-800 rounded p-3 flex justify-between items-center ${
                    segment.status === SegmentStatus.UPLOADING || segment.status === SegmentStatus.TRANSCRIBING || segment.status === SegmentStatus.RETRYING ? 'border border-blue-500' :
                    segment.status === SegmentStatus.FAILED ? 'border border-red-500' :
                    segment.status === SegmentStatus.COMPLETED ? 'border border-green-500' : ''
                  }`}>
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="flex-shrink-0">
                        <span className="text-lg">
                          {getSegmentStatusIcon(segment)}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{segment.fileName}</div>
                        <div className="text-xs text-gray-400">
                          {segment.duration.toFixed(1)}秒 | {(segment.size / 1024).toFixed(1)}KB
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {getSegmentStatusText(segment)}
                        </div>
                        {segment.transcription && (
                          <div className="text-xs text-green-400 mt-1 max-w-xs truncate">
                            "{segment.transcription.text}"
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

  
      {/* API状态和Host显示 - 仅在准备录音和录音过程中显示 */}
      {!isRecordingStopped && (
        <div className="fixed bottom-20 left-0 right-0 px-4 py-2 text-center bg-black bg-opacity-90 border-t border-gray-800">
          <div className="flex items-center justify-center space-x-2 text-xs text-gray-400">
            {/* 健康状态指示器 */}
            <div className={`w-2 h-2 rounded-full ${
              healthStatus === 'healthy' ? 'bg-green-500' :
              healthStatus === 'unhealthy' ? 'bg-red-500' :
              healthStatus === 'error' ? 'bg-red-500' :
              'bg-yellow-500 animate-pulse'
            }`}></div>
            <span>API: {API_BASE_URL}</span>
            {healthError && (
              <span className="text-red-400 ml-2">
                ({healthError})
              </span>
            )}
          </div>
        </div>
      )}

      {/* 底部导航条 */}
      <Navigation recordingState={isRecording ? 'recording' : isPaused ? 'paused' : 'idle'} />
    </div>
  );
};

export default RecordingPage;
